<template>
  <div class="page-picker">
    <EditableLabelRadio
      v-model="pageChoice"
      :disabled="disabled"
      :legend="$t('message.LINKS_TO_PAGE')"
      :items="pageLinks"
      label-key="title"
      generic-border
      @input="pagePicked"
    />
    <EditableLabelRadio
      ref="indicPicker"
      v-model="indicatorId"
      :items="indicatorsChoices"
      label-key="name"
      style="position: absolute"
      @input="indicatorPicked"
    />
    <EditableLabelRadio
      ref="gwTemplatePicker"
      v-model="gwTemplateId"
      :items="gwTemplates"
      label-key="name"
      style="position: absolute"
      @input="gwTemplatePicked"
    />
    <EditableLabelRadio
      ref="configurablePagePicker"
      v-model="configurablePageId"
      :items="cfgPages"
      label-key="name"
      style="position: absolute"
      @input="configurablePagePicked"
    />
    <div class="v-centerer page-picker__buttons">
      <RouterLink
        v-if="seePage && seePageLink"
        :to="seePageLink"
        target="_blank"
        exact
        class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue"
      >
        <Icon>{{ link }}</Icon>
        <span>{{ $t('message.SEE_PAGE') }}</span>
      </RouterLink>
      <button
        v-if="pickedPageOptions"
        class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue"
        @click="editOptions"
      >
        <Icon>{{ editMedium }}</Icon>
        <span>{{ $t('message.CHOOSE_SETTINGS') }}</span>
      </button>
    </div>
    <Modal
      :value="editingOptions && !!pickedPageOptions"
      class="options-edition"
      large
      @input="editingOptions = $event"
    >
      <ComponentsList
        ref="options"
        class="options-edition__list"
        :value="pickedPageOptions"
        send-callbacks
        @edit="optionsEdited"
      />
    </Modal>
  </div>
</template>

<script>
/**
 * Uses an EditableLabelRadio to allow the user to select a page amongst Shizen's pages;
 * User might also want to edit the default query strings of the page.
 * Eg: problems' filters
 *
 * @vue-prop {Object} value - Meeting step data. Contain following attributes:
 *                    {Number} 'pageId': (mandatory) ID value as set in SPage.js
 *                    {String} 'page': (deprecated) corresponds to the 'link' attr in SPage items
 *                    {Object} 'pageOptions': If the page features some options as query params
 *                    {Number} 'itemId': Only relevant for detailed indic, gw template and cfg page
 * @vue-prop {Boolean} seePage - Display a link to visualize what he page would be after selecting params
 * @vue-prop {Boolean} disabled - Prevents editing the page
 */

import { mapState, createNamespacedHelpers } from 'vuex';
import { editMedium, link } from '@/assets/icons';
// Import components
import CloseButton from '@/components/close-button.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
// Mixins
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
// Vuex
import { GET_CFG_PAGES, GET_LIBRARY_INDICATORS } from '@/store/mutation-types';
import { FETCH_CFG_PAGES, FETCH_INDICATORS_ONLY, FETCH_LIBRARY_INDICATORS } from '@/store/action-types';
import { GET_GW_TEMPLATES } from '@/store/modules/gemba-walks/mutation-types';
import { FETCH_GW_TEMPLATES } from '@/store/modules/gemba-walks/action-types';
// Utils
import SPage from '@/utils/classes/SPage';

const { mapState: gwMapState, mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  name: 'PagePicker',

  components: {
    CloseButton,
    ComponentsList: () => import('@/components/components-list.vue'),
    EditableLabelRadio,
    Icon,
    Modal,
  },

  mixins: [mixinMeetingPages],

  props: {
    disabled: Boolean,
    value: {
      type: Object,
      required: true,
    },
    seePage: Boolean,
    fromConfig: Boolean,
  },

  data() {
    return {
      indicatorId: 0,
      gwTemplateId: 0,
      configurablePageId: 0,
      editingOptions: false,
      editMedium,
      link,
    };
  },

  computed: {
    /** Link to allow user to visit the choosen page */
    seePageLink() {
      return this.getPageLink(this.value);
    },
    /** Page picked by the user as an item of pageLinks */
    currentPage() {
      // `page` is deprecated as of v0.3.22. Use pageId only
      const { pageId, page } = this.value;
      let currentPageData;
      if (typeof pageId === 'undefined') {
        // eslint-disable-next-line no-return-assign
        this.pageLinks.nestedFind('radioItems', p => p.olderId === page && (currentPageData = p));
      } else {
        // eslint-disable-next-line no-return-assign
        this.pageLinks.nestedFind('radioItems', p => p.id === pageId && (currentPageData = p));
      }
      return currentPageData;
    },
    pageChoice: {
      get() {
        return this.currentPage ? this.currentPage.id : 0;
      },
      set(pageId) {
        this.$set(this.value, 'pageId', pageId);
        // Reset any option
        this.$set(this.value, 'pageOptions', {});
        delete this.value.itemId;
        // input has never been used as it seems
        delete this.value.input;
      },
    },
    /**
     * Picked page object from pageLinks computed
     * TO add options to a page, the page must be listed in the switch case and return an array of components
     *
     * @return {Array} - Components array, see mixins/page-options/*.vue
     */
    pickedPageOptions() {
      switch (this.pageChoice) {
        case SPage.PROBLEMS.id: return this.pbOptions;
        case SPage.PENDING.id: return this.pendOptions;
        case SPage.ACTION_PLANS.id: return this.actOptions;
        case SPage.INDICATORS.id: return this.idcOptions;
        case SPage.STATS.id: return this.statsOptions;
        case SPage.MGT_INDICATORS.id: return this.mgtIdcOptions;
        case SPage.MGT_PROBLEMS.id:
        case SPage.MGT_ACTIONS.id:
        case SPage.MGT_STATS.id:
          return this.mgtPagesOptions;
        case SPage.INFORMATIONS.id: return this.infosOptions;
        case SPage.DASHBOARD.id: return this.dashboardOptions;
        case SPage.GW_TEMPLATES.id: return this.gwtOptions;
        case SPage.GW_CHECKLISTS.id: return this.gwChecklistsOptions;
        case SPage.GW_COMPLETED_LIST.id: return this.completedGwOptions;
        case SPage.GW_STATS.id: return this.gwStatsOptions;
        default: return null;
      }
    },
    /** Overridable computed from page-options mixins. Contains all the current option values */
    allPageOptions() {
      return this.value.pageOptions;
    },
    /** */
    indicatorsChoices() {
      return this.fromConfig
        ? this.libraryIndicators.map(theme => ({ ...theme, title: theme.name, radioItems: theme.indicators }))
        : this.indicators;
    },
    currentTeamId() {
      return this.$store.state.currentTeam && this.$store.state.currentTeam.id || 0;
    },
    indicators() {
      return (this.indicatorsArrayPerTeam[`${this.currentTeamId}`] || {}).data || [];
    },
    indicatorsLoading() {
      return (this.indicatorsArrayPerTeam[`${this.currentTeamId}`] || {}).loading;
    },
    // mixinGwStatsOptions overrides
    componentsListRefs() {
      return this.$refs.options.$refs;
    },
    ...mapState({
      cfgPages: state => state[GET_CFG_PAGES.stateKey] || [],
      cfgPagesLoading: state => state[GET_CFG_PAGES.loadingKey],
      libraryIndicators: state => state[GET_LIBRARY_INDICATORS.stateKey] || [],
      libraryIndicatorsLoading: state => state[GET_LIBRARY_INDICATORS.loadingKey],
      indicatorsArrayPerTeam: state => state.indicatorsArrayPerTeam,
    }),
    ...gwMapState({
      gwTemplates: state => state[GET_GW_TEMPLATES.stateKey] || [],
      gwTemplatesLoading: state => state[GET_GW_TEMPLATES.loadingKey],
    }),
  },

  created() {
    if (this.fromConfig) {
      if (!this.libraryIndicatorsLoading) {
        this.$store.dispatch(FETCH_LIBRARY_INDICATORS);
      }
    } else {
      if (!this.indicatorsLoading) {
        this.$store.dispatch(FETCH_INDICATORS_ONLY);
      }
      if (!this.gwTemplatesLoading) {
        this.FETCH_GW_TEMPLATES();
      }
    }
    if (!this.cfgPagesLoading) {
      this.$store.dispatch(FETCH_CFG_PAGES);
    }
  },

  methods: {
    /** triggered when a page has been picked from editable label */
    pagePicked(pageId) {
      // TODO: Create an options modal to edit query string parameters and
      // Trigger automatically the modal if indic, gw or cfg page
      if (pageId === SPage.DETAILED_INDICATOR.id || pageId === SPage.INDICATOR_INPUT.id) {
        this.$refs.indicPicker.edit();
      } else if (pageId === SPage.GW_COMPLETED.id) {
        this.$refs.gwTemplatePicker.edit();
      } else if (pageId === SPage.CFG_PAGE.id) {
        this.$refs.configurablePagePicker.edit();
      }
      this.$emit('input', this.value);
    },
    /** Edit the chosen page query string parameters */
    editOptions() {
      this.editingOptions = true;
    },
    /** At least one option was edited */
    optionsEdited(option) {
      this.checkPageOptions();
      this.$set(this.value.pageOptions, option.key, option.value);
      this.$emit('input', this.value);
    },
    /** Make sure pageOptions property is set */
    checkPageOptions() {
      if (!(this.value.pageOptions instanceof Object) || this.value.pageOptions instanceof Array) {
        this.$set(this.value, 'pageOptions', {});
      }
    },
    /** Triggered when the indicator has been picked from hidden editable label */
    indicatorPicked() {
      const { pageId } = this.value;
      if ((pageId === SPage.DETAILED_INDICATOR.id || pageId === SPage.INDICATOR_INPUT.id) && this.indicatorId) {
        this.$set(this.value, 'itemId', this.indicatorId);
        this.$set(this.value.pageOptions, 'itemId', this.indicatorId);
      }
      this.indicatorId = 0;
      this.$emit('input', this.value);
    },
    /** Triggered when the gw template has been picked from hidden editable label */
    gwTemplatePicked() {
      if (this.value.pageId === SPage.GW_COMPLETED.id && this.gwTemplateId) {
        this.$set(this.value, 'itemId', this.gwTemplateId);
        this.$set(this.value.pageOptions, 'itemId', this.gwTemplateId);
      }
      this.gwTemplateId = 0;
      this.$emit('input', this.value);
    },
    /** Triggered when the cfg page has been picked from hidden editable label */
    configurablePagePicked() {
      if (this.value.pageId === SPage.CFG_PAGE.id && this.configurablePageId) {
        this.$set(this.value, 'itemId', this.configurablePageId);
        this.$set(this.value.pageOptions, 'itemId', this.configurablePageId);
      }
      this.configurablePageId = 0;
      this.$emit('input', this.value);
    },
    ...gwMapActions({
      FETCH_GW_TEMPLATES,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.page-picker {

  .options-edition {
    text-align: initial;

    &__list {
      padding: 1em 4em;
      box-sizing: border-box;
    }

    &.dark-overlay .dark-overlay-content {
      width: auto;
      min-width: 40%;
    }

    h2.basic-sub-title {
      font-size: 1.4rem;
    }
  }

  &__buttons {
    margin-top: 1rem;

    > :first-child {
      margin-right: 1rem;
    }
  }
}
</style>
